import type { EqualizerChartTooltipData } from '@/common/components/molecules/equalizerChart/core/equalizerChart.types';
import type {
  ServerData,
  ServerOption,
} from '@/common/components/molecules/serverNodeGraph/serverNodeGraph.types';
import type { TopologyWidgetTooltipData } from '@/dashboard/features/widgets/topologyWidget/types/topology.types';
import type {
  AlertFontColorParams,
  AlertRuleCriteriaParams,
} from '@/dashboard/features/widgetsAlert/composables/useAlertHighlightOption';
import type { IconType } from '@/types/icon.types';
import type { GaugeWidgetData } from '@/dashboard/features/widgets/gauge/widget/domain/gaugeWidget.types';
import type { GaugeChartHit } from '@/dashboard/features/widgets/gauge/widget/domain/worker/workerCmd.types';
import type { WidgetSeriesType } from '@/dashboard/shared/types/widgets.types';
import type { ExclusiveStyle1ChartTooltipData } from '@/common/components/molecules/exclusiveStyle1Chart/exclusiveStyle1Chart.type';
import type { StatusHexaWidgetTooltipData } from '@/dashboard/features/widgets/statusEvent/widget/domain/types';
import type { ChartContextElementData } from '../types/chartContextElement.types';
import dayjs from 'dayjs';
import { i18n } from '@/common/locale';
import { numberWithCommaNoExponent } from '@/common/utils/core/number/format';
import {
  ChartYAxisUnitConverter,
  UnitConverter,
} from '@/dashboard/features/chartDataHandler/domain/unit';
import type { UnitOverride } from '@/dashboard/features/chartDataHandler/domain/unit/types';
import type {
  ContextType,
  DefaultInfoItem,
  DefaultChartContextData,
  AlertLineChartContextData,
  EqualizerChartContextData,
  ServerChartContextData,
  TopologyChartContextData,
  GaugeChartContextData,
  ExclusiveStyle1ChartContextData,
  StatusHexaChartContextData,
} from '../types/chartContext.types';
import type { ChartPopupItem } from '../domain/chartPopupItem';
import type { QueryStatTooltipBadge } from '@/dashboard/features/widgets/common/domain/queryStatTimezone';

/**
 * ChartPopupItem 의 value/unit/formattedValue 를 표시 정책에 따라 문자열로 변환.
 * - `unit`이 있으면 `${천 단위 콤마 적용된 value} ${unit}`
 * - `unit`이 없고 `formattedValue`가 있으면 `formattedValue` 그대로 (위젯이 이미 포매팅한 값)
 * - 둘 다 없으면 raw `value`에 천 단위 콤마만 적용 (`numberWithCommaNoExponent`: 지수표기 회피)
 * - 모두 없으면 `fallback` 반환
 */
export const formatPopupItemDisplayValue = (
  popupItem: Pick<ChartPopupItem, 'value' | 'unit' | 'formattedValue'> | undefined | null,
  fallback: string = '-',
): string => {
  if (popupItem?.unit != null) {
    if (popupItem.value == null) return fallback;
    const formatted = String(numberWithCommaNoExponent(popupItem.value));
    return popupItem.unit ? `${formatted} ${popupItem.unit}` : formatted;
  }
  if (popupItem?.formattedValue != null) {
    return popupItem.formattedValue;
  }
  return popupItem?.value != null ? String(numberWithCommaNoExponent(popupItem.value)) : fallback;
};

/**
 * chartPopup의 value 와 unit 을 계산.
 *
 * 정책:
 * - **value**: raw 값 그대로 사용 (단위 변환 없음). 천단위 콤마는 표시 단계에서 적용.
 * - **unit**: 사용자가 설정한 `unitOverride.overrideUnit` 적용. 미설정 시 원본 API 단위.
 * - 차트의 **고정단위(`unitScaleTo`) / 자동스케일(`autoScaleUnit`)** 은 chartPopup 에 반영하지 않는다.
 *   (차트만의 시각적 표시 옵션이며 chartPopup 은 raw + 사용자가 의도한 재정의 단위만 따른다)
 * - **소수점, 카운트(K·M)** 등 표시 가공도 미적용.
 *
 * 구현: `ChartYAxisUnitConverter.getUnitInfo` 의 `baseUnit` 을 사용 — `unitOverride.overrideUnit`
 * 이 그대로 baseUnit 으로 반환되며, autoScaleUnit/unitScaleTo 는 baseUnit 결정에 영향 없음.
 *
 * `apiUnit` 은 위젯에 따라 두 형태로 들어올 수 있다:
 * - raw API 단위 string (예: `'%'`, `'ms'`, `'KB'`) — Gauge / TopList / Pie 등
 * - 이미 표준화된 `AllUnitOptionValues` (예: `'percent (%)'`, `'millisecond (ms)'`) — TimeSeries `getDefaultUnit` 결과
 *
 * 두 경우 모두 처리하기 위해 `convertApiUnit` 결과가 매핑되지 않으면 원본 `apiUnit` 을 그대로 사용한다.
 */
export const computeChartPopupValueAndUnit = (
  rawValue: number | string | null | undefined,
  apiUnit: string | null | undefined,
  unitOverride: UnitOverride | null | undefined,
): { value: number | null; unit: string } => {
  const rawUnit = apiUnit ?? 'none';
  // ApiUnitMap 매핑이 없으면 이미 표준 단위 키이거나 알 수 없는 단위이므로 rawUnit 자체를 사용
  const defaultUnit = UnitConverter.convertApiUnit(rawUnit) ?? rawUnit;
  const { baseUnit } = ChartYAxisUnitConverter.getUnitInfo(unitOverride ?? undefined, defaultUnit);
  const unitAbbr = UnitConverter.getUnitAbbreviation(baseUnit);

  if (rawValue == null) return { value: null, unit: unitAbbr };
  if (typeof rawValue === 'number') {
    return { value: Number.isNaN(rawValue) ? null : rawValue, unit: unitAbbr };
  }
  const parsed = Number(rawValue);
  return { value: Number.isNaN(parsed) ? null : parsed, unit: unitAbbr };
};

/**
 * 기본 contextsByType 생성
 */
export const createEmptyContextsByType = (): Record<
  ContextType,
  { title: string; contexts: ChartContextElementData[] }
> => ({
  detail: { title: '', contexts: [] },
  link: { title: '', contexts: [] },
});

/**
 * Default Chart Context 생성
 */
export const createDefaultChartContext = (
  infoItems: DefaultInfoItem[] = [],
): DefaultChartContextData => ({
  type: 'default',
  infoItems,
});

/**
 * ChartPopupItem + label로부터 Default Chart Context 생성 (store 기본 콜백용)
 */
export const createDefaultChartContextFromPopupItem = (
  popupItem?: ChartPopupItem,
  label?: number | null,
): DefaultChartContextData => {
  const { t } = i18n.global;
  const items: DefaultInfoItem[] = [];

  if (label != null) {
    items.push({ label: t('WORD.TIME_POINT'), value: dayjs(label).format('HH:mm:ss') });
  }

  items.push({
    label: t('WORD.VALUE'),
    value: formatPopupItemDisplayValue(popupItem),
    variant: 'emphasis',
  });

  return createDefaultChartContext(items);
};

/**
 * Alert Line Chart Context 생성 — 더블클릭 시점의 레벨별 값을 호버 툴팁과 동일 형태로 표시한다.
 */
export const createAlertLineChartContext = (options: {
  label: number | null;
  items: AlertLineChartContextData['items'];
}): AlertLineChartContextData => ({
  type: 'alertLine',
  label: options.label,
  items: options.items,
});

/**
 * Equalizer Chart Context 생성
 */
export const createEqualizerChartContext = (options?: {
  chartClickedColor?: string;
  info?: EqualizerChartTooltipData;
  maxLabelWidth?: string;
  formatter?: (value: number) => string;
  queryStatTooltipBadge?: QueryStatTooltipBadge | null;
}): EqualizerChartContextData => ({
  type: 'equalizer',
  chartClickedColor: options?.chartClickedColor,
  contextsByType: createEmptyContextsByType(),
  info: options?.info,
  maxLabelWidth: options?.maxLabelWidth,
  formatter: options?.formatter,
  queryStatTooltipBadge: options?.queryStatTooltipBadge,
});

/**
 * Server Chart Context 생성
 */
export const createServerChartContext = (options?: {
  chartClickedColor?: string;
  info?: ServerData | null;
  option?: {
    aliasSet?: ServerOption['aliasSet'];
    colorSet?: ServerOption['colorSet'];
  };
}): ServerChartContextData => ({
  type: 'server',
  chartClickedColor: options?.chartClickedColor,
  contextsByType: createEmptyContextsByType(),
  info: options?.info,
  option: options?.option,
});

/**
 * Topology Chart Context 생성
 */
export const createTopologyChartContext = (options?: {
  chartClickedColor?: string;
  info?: TopologyWidgetTooltipData | null;
}): TopologyChartContextData => ({
  type: 'topology',
  chartClickedColor: options?.chartClickedColor,
  contextsByType: createEmptyContextsByType(),
  info: options?.info,
});

/**
 * Gauge Chart Context 생성
 */
export const createGaugeChartContext = (options: {
  chartClickedColor?: string;
  info?: {
    data: GaugeWidgetData;
    seriesType?: WidgetSeriesType;
    hoveredHit?: GaugeChartHit | null;
  };
  formatter?: {
    alertFontColor?: (item: AlertFontColorParams) => string;
    alertRuleCriteriaIconList?: (item: AlertRuleCriteriaParams) => IconType[];
  };
}): GaugeChartContextData => ({
  type: 'gauge',
  chartClickedColor: options.chartClickedColor,
  contextsByType: createEmptyContextsByType(),
  info: options.info,
  formatter: options.formatter,
});

/**
 * ExclusiveStyle1 Chart Context 생성
 */
export const createExclusiveStyle1ChartContext = (options?: {
  chartClickedColor?: string;
  info?: ExclusiveStyle1ChartTooltipData;
  formatter?: {
    alertFontColor?: (item: AlertFontColorParams) => string;
  };
  colorList?: string[];
}): ExclusiveStyle1ChartContextData => ({
  type: 'exclusiveStyle1',
  chartClickedColor: options?.chartClickedColor,
  contextsByType: createEmptyContextsByType(),
  info: options?.info,
  formatter: options?.formatter,
  colorList: options?.colorList,
});

/**
 * StatusHexa Chart Context 생성
 */
export const createStatusHexaChartContext = (options?: {
  chartClickedColor?: string;
  info?: StatusHexaWidgetTooltipData;
}): StatusHexaChartContextData => ({
  type: 'statusHexa',
  chartClickedColor: options?.chartClickedColor,
  contextsByType: createEmptyContextsByType(),
  info: options?.info,
});
